import { VAL, SUITS } from './ScoringConstants.js';

/**
 * Generate all k-combinations from an array.
 */
export function combinations(arr, k) {
  const out = [];
  const rec = (start, path) => {
    if (path.length === k) {
      out.push(path.slice());
      return;
    }
    for (let i = start; i < arr.length; i++) {
      path.push(arr[i]);
      rec(i + 1, path);
      path.pop();
    }
  };
  rec(0, []);
  return out;
}

/**
 * Check if ranks form a consecutive run.
 */
export function isConsecutiveRun(rs, allowWrap = false) {
  const n = rs.length;
  if (n < 3) return false;
  const set = new Set(rs);
  if (set.size !== n) return false;
  if (!allowWrap) {
    const sorted = rs.slice().sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) return false;
    }
    return true;
  }
  for (const start of set) {
    const prev = start === 1 ? 13 : start - 1;
    if (!set.has(prev)) {
      let ok = true;
      let curr = start;
      for (let step = 1; step < n; step++) {
        curr = curr === 13 ? 1 : curr + 1;
        if (!set.has(curr)) {
          ok = false;
          break;
        }
      }
      if (ok) return true;
    }
  }
  return false;
}

function cardRank(c) {
  return (c && c.playAs && c.playAs.r) || (c ? (c.r !== undefined ? c.r : (c.rank !== undefined ? c.rank : 0)) : 0);
}

function cardSuit(c) {
  return (c && c.playAs && c.playAs.s) || (c ? (c.s !== undefined ? c.s : (c.suit !== undefined ? c.suit : 'S')) : 'S');
}

export function isSpadeCard(c) {
  if (!c) return false;
  const raw = (c.playAs && c.playAs.s) || c.s || c.suit || '';
  const s = String(raw).trim().toLowerCase();
  return s === 's' || s.startsWith('spade') || s === '♠';
}

function isCardWild(c, isCrib = false) {
  if (!c || (!c.wild && !c.isWild)) return false;
  if (isCrib) return true;
  return !c.playAs;
}

/**
 * Pure Cribbage Scoring Engine.
 * Calculates pegging points and hand/crib show points according to classic and crazy rules.
 */
export class CribbageScorer {
  /**
   * Evaluates if a slice of played cards forms a valid single or multiple run.
   * @param {Array<Object>} slice
   * @param {boolean} [allowWrap=false]
   * @param {boolean} [allowMultiple=false]
   * @returns {Object|null}
   */
  static evaluateRunSlice(slice, allowWrap = false, allowMultiple = false) {
    if (!slice || slice.length < 3) return null;
    const rs = slice.map(c => cardRank(c));
    const byRank = {};
    for (const r of rs) {
      if (r === 0 || r === undefined) return null;
      byRank[r] = (byRank[r] || 0) + 1;
    }
    const uniqueRanks = Object.keys(byRank).map(Number);
    const seqLen = uniqueRanks.length;
    if (seqLen < 3) return null;

    let prod = 1;
    for (const r of uniqueRanks) {
      prod *= byRank[r];
    }

    if (prod > 1 && !allowMultiple) {
      return null;
    }

    if (!isConsecutiveRun(uniqueRanks, allowWrap)) {
      return null;
    }

    let why = 'run of ' + seqLen;
    if (prod === 2) why = 'double run of ' + seqLen;
    else if (prod === 3) why = 'triple run of ' + seqLen;
    else if (prod === 4) why = 'quadruple run of ' + seqLen;
    else if (prod > 4) why = prod + 'x run of ' + seqLen;

    return {
      len: slice.length,
      seqLen,
      prod,
      points: seqLen * prod,
      why
    };
  }

  /**
   * Score pegging points for the latest card played on a pile.
   */
  static scorePegging(pile, options = {}) {
    if (!pile || !pile.length) return [];
    const pts = [];
    const count = pile.reduce((s, c) => s + VAL(cardRank(c)), 0);

    if (count === 15) pts.push({ n: 2, why: 'fifteen' });
    if (count === 19 && options.opt19sFor2) pts.push({ n: 2, why: 'nineteen' });
    if (count === 31) pts.push({ n: options.opt31sFor5 ? 5 : 2, why: 'thirty-one' });

    const lastR = cardRank(pile[pile.length - 1]);
    let same = 0;
    for (let i = pile.length - 1; i >= 0; i--) {
      if (cardRank(pile[i]) === lastR) same++;
      else break;
    }
    if (same >= 2) {
      const n = same * (same - 1);
      const why = same === 2 ? 'pair' : same === 3 ? 'pair royal' : same === 4 ? 'double pair royal' : same + ' of a kind';
      pts.push({ n, why });
    }

    let bestRun = null;
    const allowWrap = Boolean(options.optRoundAroundTheWorld);
    const allowMultiple = Boolean(options.optMultipleRunsInPegging || options.optMultipleRuns);

    for (let len = pile.length; len >= 3; len--) {
      const slice = pile.slice(pile.length - len);
      const res = CribbageScorer.evaluateRunSlice(slice, allowWrap, allowMultiple);
      if (res) {
        if (!bestRun || res.points > bestRun.points || (res.points === bestRun.points && res.len > bestRun.len)) {
          bestRun = res;
        }
      }
    }
    if (bestRun) {
      pts.push({ n: bestRun.points, why: bestRun.why });
    }

    if (options.optFlushInPlay && pile.length >= 3) {
      const lastSuit = cardSuit(pile[pile.length - 1]);
      let flushCount = 0;
      for (let i = pile.length - 1; i >= 0; i--) {
        if (cardSuit(pile[i]) === lastSuit) {
          flushCount++;
        } else {
          break;
        }
      }
      if (flushCount >= 3) {
        pts.push({ n: flushCount, why: 'flush of ' + flushCount });
      }
    }

    return pts;
  }

  /**
   * Score hand or crib (exact core calculation with no wild variables).
   */
  static scoreHandCore(cards, starter, isCrib = false, options = {}) {
    const starterList = Array.isArray(starter) ? starter.filter(Boolean) : (starter ? [starter] : []);
    const all = starterList.length ? cards.concat(starterList) : cards.slice();
    const detail = {
      fifteens: 0,
      nineteens: 0,
      thirtyOnes: 0,
      pairs: 0,
      runs: 0,
      flush: 0,
      nobs: 0,
      total: 0,
      notes: [],
      lines: []
    };

    const n = all.length;
    // Combinations for 15s, 19s, 31s
    for (let mask = 1; mask < (1 << n); mask++) {
      let sum = 0;
      const combo = [];
      for (let i = 0; i < n; i++) {
        if (mask & (1 << i)) {
          sum += VAL(cardRank(all[i]));
          combo.push(all[i]);
        }
      }
      if (sum === 15) {
        detail.fifteens += 2;
        detail.lines.push({ n: 2, why: 'Fifteen', cards: combo });
      }
      if (options.opt19sFor2 && sum === 19) {
        detail.nineteens = (detail.nineteens || 0) + 2;
        detail.lines.push({ n: 2, why: 'Nineteen', cards: combo, isBonus: true });
      }
      if (options.opt31sFor5 && sum === 31) {
        detail.thirtyOnes = (detail.thirtyOnes || 0) + 5;
        detail.lines.push({ n: 5, why: 'Thirty-one', cards: combo, isBonus: true });
      }
    }

    if (detail.fifteens) detail.notes.push(`${detail.fifteens / 2} fifteen${detail.fifteens > 2 ? 's' : ''} (${detail.fifteens})`);
    if (detail.nineteens) {
      const cnt = detail.nineteens / 2;
      detail.notes.push(`${cnt} nineteen${cnt > 1 ? 's' : ''} (${detail.nineteens})`);
    }
    if (detail.thirtyOnes) {
      const cnt = detail.thirtyOnes / 5;
      detail.notes.push(`${cnt} thirty-one${cnt > 1 ? 's' : ''} (${detail.thirtyOnes})`);
    }

    // Pairs
    const byRank = {};
    for (const c of all) {
      const r = cardRank(c);
      byRank[r] = (byRank[r] || []).concat([c]);
    }
    for (const r in byRank) {
      const group = byRank[r];
      const k = group.length;
      if (k >= 2) {
        const pts = k * (k - 1);
        detail.pairs += pts;
        const label = k === 2 ? 'Pair' : k === 3 ? 'Pair royal' : k === 4 ? 'Double pair royal' : k + ' of a kind';
        const pairCombos = combinations(group, 2);
        if (pairCombos.length) {
          for (const pair of pairCombos) {
            detail.lines.push({ n: 2, why: k === 2 ? 'Pair' : label, cards: pair });
          }
        } else {
          detail.lines.push({ n: pts, why: label, cards: group });
        }
      }
    }
    if (detail.pairs) detail.notes.push(`pairs (${detail.pairs})`);

    // Runs
    const ranksPresent = Object.keys(byRank).map(Number).sort((a, b) => a - b);
    const runSequences = [];
    if (options.optRoundAroundTheWorld) {
      const rankSet = new Set(ranksPresent);
      for (const r of ranksPresent) {
        const prev = r === 1 ? 13 : r - 1;
        if (!rankSet.has(prev)) {
          const seq = [r];
          let curr = r;
          while (seq.length < 13) {
            const next = curr === 13 ? 1 : curr + 1;
            if (!rankSet.has(next)) break;
            seq.push(next);
            curr = next;
          }
          if (seq.length >= 3) runSequences.push(seq);
        }
      }
    } else {
      let i = 0;
      while (i < ranksPresent.length) {
        let j = i;
        while (j + 1 < ranksPresent.length && ranksPresent[j + 1] === ranksPresent[j] + 1) j++;
        const len = j - i + 1;
        if (len >= 3) {
          const seq = [];
          for (let t = i; t <= j; t++) seq.push(ranksPresent[t]);
          runSequences.push(seq);
        }
        i = j + 1;
      }
    }

    for (const seq of runSequences) {
      const len = seq.length;
      let prod = 1;
      for (const r of seq) prod *= byRank[r].length;
      detail.runs += len * prod;
      const groups = seq.map(r => byRank[r]);
      const runCombos = [];
      const walk = (gi, acc) => {
        if (gi === groups.length) {
          runCombos.push(acc.slice());
          return;
        }
        for (const card of groups[gi]) {
          acc.push(card);
          walk(gi + 1, acc);
          acc.pop();
        }
      };
      walk(0, []);
      if (runCombos.length) {
        for (const rc of runCombos) detail.lines.push({ n: len, why: 'Run of ' + len, cards: rc });
      } else {
        const runCards = [];
        for (const r of seq) runCards.push(...byRank[r]);
        detail.lines.push({ n: len * prod, why: 'Run of ' + len, cards: runCards });
      }
    }
    if (detail.runs) detail.notes.push(`runs (${detail.runs})`);

    // Flush
    if (cards.length) {
      const suitCounts = {};
      for (const c of cards) {
        const su = cardSuit(c);
        suitCounts[su] = (suitCounts[su] || 0) + 1;
      }
      const handSuits = Object.entries(suitCounts).sort((a, b) => b[1] - a[1]);
      const topSuit = handSuits[0];
      if (topSuit && topSuit[1] === cards.length && cards.length >= 4) {
        const matchingStarters = starterList.filter(s => cardSuit(s) === topSuit[0]);
        if (isCrib || options.optFiveCardFlushOnly) {
          if (matchingStarters.length === starterList.length && starterList.length > 0) {
            detail.flush = cards.length + matchingStarters.length;
          }
        } else {
          detail.flush = cards.length + matchingStarters.length;
        }
        if (detail.flush) {
          detail.notes.push(`flush (${detail.flush})`);
          const flushCards = cards.filter(c => cardSuit(c) === topSuit[0]).concat(matchingStarters);
          detail.lines.push({ n: detail.flush, why: 'Flush', cards: flushCards });
        }
      }
    }

    // His Nobs (Jack in hand matching starter suit)
    if (starterList.length) {
      for (const st of starterList) {
        const jack = cards.find(c => cardRank(c) === 11 && cardSuit(c) === cardSuit(st));
        if (jack) {
          detail.nobs += 1;
          detail.lines.push({ n: 1, why: 'Nobs', cards: [jack, st] });
        }
      }
    }
    if (detail.nobs) {
      detail.notes.push(`nobs (${detail.nobs})`);
    }

    // Bonuses: Color & Parity
    const isRed = c => cardSuit(c) === 'H' || cardSuit(c) === 'D';
    const isBlack = c => cardSuit(c) === 'S' || cardSuit(c) === 'C';
    const nAll = all.length;
    if (options.optAllRed && all.every(c => isRed(c))) {
      detail.allRed = nAll;
      detail.notes.push(`all red (${nAll})`);
      detail.lines.push({ n: nAll, why: 'All red', cards: all.slice(), isBonus: true });
    }
    if (options.optAllBlack && all.every(c => isBlack(c))) {
      detail.allBlack = nAll;
      detail.notes.push(`all black (${nAll})`);
      detail.lines.push({ n: nAll, why: 'All black', cards: all.slice(), isBonus: true });
    }
    if (options.optAllOdd && all.every(c => cardRank(c) % 2 === 1)) {
      detail.allOdd = nAll;
      detail.notes.push(`all odd (${nAll})`);
      detail.lines.push({ n: nAll, why: 'All odd', cards: all.slice(), isBonus: true });
    }
    if (options.optAllEven && all.every(c => cardRank(c) % 2 === 0)) {
      detail.allEven = nAll;
      detail.notes.push(`all even (${nAll})`);
      detail.lines.push({ n: nAll, why: 'All even', cards: all.slice(), isBonus: true });
    }

    if (options.optAllDifferentSuits) {
      let hasClub = false, hasDiamond = false, hasHeart = false, hasSpade = false;
      cards.forEach(c => {
        const su = cardSuit(c);
        if (su === 'C') hasClub = true;
        if (su === 'D') hasDiamond = true;
        if (su === 'H') hasHeart = true;
        if (su === 'S') hasSpade = true;
      });
      if (cards.length === 4 && hasClub && hasDiamond && hasHeart && hasSpade) {
        detail.allDifferentSuits = 4;
        detail.notes.push('all diff suits (4)');
        const diffCards = ['C', 'D', 'H', 'S'].map(s => cards.find(c => cardSuit(c) === s)).filter(Boolean);
        detail.lines.push({ n: 4, why: 'All different suits', cards: diffCards, isBonus: true });
      }
    }

    // Poker Hands
    const isFlush5 = cs => cs.length === 5 && cs.every(c => cardSuit(c) === cardSuit(cs[0]));
    const isStraight5 = cs => {
      if (cs.length !== 5) return false;
      const rs = cs.map(c => cardRank(c)).sort((a, b) => a - b);
      if (new Set(rs).size !== 5) return false;
      if (rs[4] - rs[0] === 4) return true;
      if (rs[0] === 1 && rs[1] === 10 && rs[2] === 11 && rs[3] === 12 && rs[4] === 13) return true;
      if (options.optRoundAroundTheWorld) {
        return isConsecutiveRun(rs, true);
      }
      return false;
    };
    const isRoyal5 = cs => {
      if (!isFlush5(cs)) return false;
      const set = new Set(cs.map(c => cardRank(c)));
      return [1, 10, 11, 12, 13].every(r => set.has(r));
    };
    const isFullHouse5 = cs => {
      if (!cs || cs.length < 5) return false;
      const by = {};
      for (const c of cs) {
        const r = cardRank(c);
        by[r] = (by[r] || 0) + 1;
      }
      const counts = Object.values(by).sort((a, b) => b - a);
      return counts[0] >= 3 && counts[1] >= 2;
    };

    const fives = nAll >= 5 ? combinations(all, 5) : [];
    if (options.optKH) {
      const khCard = cards.find(c => cardRank(c) === 13 && cardSuit(c) === 'H');
      if (khCard) {
        detail.kh = 5;
        detail.notes.push('suicide king (5)');
        detail.lines.push({ n: 5, why: 'Suicide king', cards: [khCard], isBonus: true });
      }
    }
    if (options.optQS) {
      const qsCard = cards.find(c => cardRank(c) === 12 && cardSuit(c) === 'S');
      if (qsCard) {
        detail.qs = 5;
        detail.notes.push('queen of spades (5)');
        detail.lines.push({ n: 5, why: 'Queen of spades', cards: [qsCard], isBonus: true });
      }
    }
    if (options.optAS) {
      const asCard = cards.find(c => cardRank(c) === 1 && cardSuit(c) === 'S');
      if (asCard) {
        detail.as = 5;
        detail.notes.push('ace of spades (5)');
        detail.lines.push({ n: 5, why: 'Ace of spades', cards: [asCard], isBonus: true });
      }
    }
    let scoredPokerRun = false;
    if (options.optRoyalFlush) {
      const rf5 = fives.find(isRoyal5);
      if (rf5) {
        scoredPokerRun = true;
        detail.royalFlush = 30;
        detail.notes.push('royal flush (30)');
        detail.lines.push({ n: 30, why: 'Royal flush', cards: rf5, isBonus: true });
      }
    }
    if (!scoredPokerRun && options.optStraightFlush) {
      const sf5 = fives.find(cs => isFlush5(cs) && isStraight5(cs));
      if (sf5) {
        scoredPokerRun = true;
        detail.straightFlush = 20;
        detail.notes.push('straight flush (20)');
        detail.lines.push({ n: 20, why: 'Straight flush', cards: sf5, isBonus: true });
      }
    }
    if (!scoredPokerRun && options.optStraight) {
      const st5 = fives.find(isStraight5);
      if (st5) {
        scoredPokerRun = true;
        detail.straight = 10;
        detail.notes.push('straight (10)');
        detail.lines.push({ n: 10, why: 'Straight', cards: st5, isBonus: true });
      }
    }
    if (options.optFullHouse) {
      const fh5 = fives.find(isFullHouse5);
      if (fh5) {
        detail.fullHouse = 10;
        detail.notes.push('full house (10)');
        detail.lines.push({ n: 10, why: 'Full house', cards: fh5, isBonus: true });
      }
    }

    detail.total = detail.fifteens + (detail.nineteens || 0) + (detail.thirtyOnes || 0) + detail.pairs + detail.runs + detail.flush + detail.nobs
      + (detail.allRed || 0) + (detail.allBlack || 0) + (detail.allOdd || 0) + (detail.allEven || 0) + (detail.allDifferentSuits || 0)
      + (detail.kh || 0) + (detail.qs || 0) + (detail.as || 0)
      + (detail.straight || 0)
      + (detail.fullHouse || 0) + (detail.straightFlush || 0) + (detail.royalFlush || 0);

    if (options.optSpades) {
      const spadeCut = starterList.some(isSpadeCard);
      const hasSpade = cards.some(isSpadeCard);
      if (hasSpade) {
        const rawPts = detail.total;
        if (rawPts > 0) {
          detail.spadesDouble = rawPts;
          detail.total = rawPts * 2;
          const noteText = spadeCut
            ? `spades cut & in ${isCrib ? 'crib' : 'hand'} (double points: +${rawPts})`
            : `spades in ${isCrib ? 'crib' : 'hand'} (double points: +${rawPts})`;
          detail.notes.push(noteText);
          const highlightCards = cards.filter(isSpadeCard).concat(starterList.filter(isSpadeCard));
          detail.lines.push({ n: rawPts, why: `Spades in ${isCrib ? 'crib' : 'hand'} double points`, cards: highlightCards, isBonus: true });
        }
      } else {
        // Dealer exception for crib: if there is a spade in dealer's hand, dealer does not get -29 for no spade in the crib
        let dealerHasSpade = false;
        if (isCrib) {
          if (options.dealerHasSpade !== undefined) {
            dealerHasSpade = Boolean(options.dealerHasSpade);
          } else if (Array.isArray(options.dealerHand)) {
            dealerHasSpade = options.dealerHand.some(isSpadeCard);
          } else {
            const st = options.state || (typeof window !== 'undefined' ? window.state : null);
            if (st) {
              const d = st.dealer;
              const dCards = (st.seatKept && st.seatKept[d]) || (st.lastShow && st.lastShow.dealer && st.lastShow.dealer.cards) || (d === 'p1' || d === 'you' ? st.youHand : st.cpuHand);
              if (Array.isArray(dCards)) {
                dealerHasSpade = dCards.some(isSpadeCard);
              }
            }
          }
        }

        if (isCrib && dealerHasSpade) {
          detail.notes.push('spade in dealer hand (no crib penalty)');
        } else {
          detail.total = -29;
          detail.spadesPenalty = -29;
          detail.notes = [`no spade in ${isCrib ? 'crib' : 'hand'} (-29)`];
          detail.lines = [{ n: -29, why: `No Spade in ${isCrib ? 'crib' : 'hand'} penalty`, cards: cards.slice(), isBonus: true }];
          detail.fifteens = 0;
          detail.nineteens = 0;
          detail.thirtyOnes = 0;
          detail.pairs = 0;
          detail.runs = 0;
          detail.flush = 0;
          detail.nobs = 0;
          detail.allRed = 0;
          detail.allBlack = 0;
          detail.allOdd = 0;
          detail.allEven = 0;
          detail.allDifferentSuits = 0;
          detail.kh = 0;
          detail.qs = 0;
          detail.as = 0;
          detail.fullHouse = 0;
          detail.straightFlush = 0;
          detail.royalFlush = 0;
          detail.straight = 0;
        }
      }
    }

    if (options.optZeroHand && !isCrib && detail.total === 0) {
      detail.zeroHand = -19;
      detail.notes.push('zero hand (-19)');
      detail.lines.push({ n: -19, why: 'Zero hand penalty', cards: all.slice(), isBonus: true });
      detail.total += -19;
    }
    if (options.optZeroCrib && isCrib && detail.total === 0) {
      if (!options.optRockCrib) {
        detail.zeroCrib = -19;
        detail.notes.push('zero crib (-19)');
        detail.lines.push({ n: -19, why: 'Zero crib penalty', cards: all.slice(), isBonus: true });
        detail.total += -19;
      } else {
        detail.zeroCrib = 19;
        detail.notes.push('rock crib bonus (19)');
        detail.lines.push({ n: 19, why: 'Rock crib bonus', cards: all.slice(), isBonus: true });
        detail.total += 19;
      }
    }

    return detail;
  }

  /**
   * Score hand with automatic resolution of wild cards (Jokers) to maximize (or minimize if lowball) points.
   */
  static scoreHand(cards = [], starter = null, isCrib = false, options = {}, precise = true) {
    const allCards = cards.slice();
    const wilds = allCards.filter(c => isCardWild(c, isCrib));
    const starterList = Array.isArray(starter) ? starter.filter(Boolean) : (starter ? [starter] : []);
    for (const st of starterList) {
      if (isCardWild(st, false)) wilds.push(st);
    }

    if (!wilds.length) {
      return CribbageScorer.scoreHandCore(allCards, starter, isCrib, options);
    }

    const lowball = options.goal === 'low';
    let best = null;
    const suitList = (precise === false || wilds.length >= 3)
      ? [(allCards.find(c => !isCardWild(c, isCrib)) || { s: 'S' }).s]
      : SUITS.slice();

    const rec = (wildIdx, wildAssignments) => {
      if (wildIdx === wilds.length) {
        const mappedCards = allCards.map(c => {
          const w = wildAssignments.find(a => a.card.id === c.id);
          return w ? { ...c, r: w.r, s: w.s, playAs: { r: w.r, s: w.s } } : c;
        });
        const mappedStarter = Array.isArray(starter)
          ? starter.map(st => {
              if (isCardWild(st, false)) {
                const w = wildAssignments.find(a => a.card.id === st.id);
                return w ? { ...st, r: w.r, s: w.s, playAs: { r: w.r, s: w.s } } : st;
              }
              return st;
            })
          : (starter && isCardWild(starter, false)
              ? (() => {
                  const w = wildAssignments.find(a => a.card.id === starter.id);
                  return w ? { ...starter, r: w.r, s: w.s, playAs: { r: w.r, s: w.s } } : starter;
                })()
              : starter);

        const res = CribbageScorer.scoreHandCore(mappedCards, mappedStarter, isCrib, options);
        if (!best) {
          best = res;
        } else if (lowball ? (res.total < best.total) : (res.total > best.total)) {
          best = res;
        }
        return;
      }

      const targetWild = wilds[wildIdx];
      for (let r = 1; r <= 13; r++) {
        for (const s of suitList) {
          wildAssignments.push({ card: targetWild, r, s });
          rec(wildIdx + 1, wildAssignments);
          wildAssignments.pop();
        }
      }
    };

    rec(0, []);
    if (best && wilds.length) {
      best.notes = (best.notes || []).concat(['wild']);
    }
    return best || CribbageScorer.scoreHandCore(allCards, starter, isCrib, options);
  }

  static pegTotal(pile, settings = {}) {
    return CribbageScorer.scorePegging(pile, settings).reduce((s, p) => s + p.n, 0);
  }

  static scoreHeels(starter) {
    if (!starter) return 0;
    const starterList = Array.isArray(starter) ? starter.filter(Boolean) : [starter];
    let heels = 0;
    for (const st of starterList) {
      const r = st.rank !== undefined ? st.rank : (st.r || 0);
      if (r === 11) heels += 2;
    }
    return heels;
  }

  static pegPotential(cards = []) {
    let v = 0;
    let fives = 0, tens = 0, lows = 0;
    for (const c of cards) {
      const isW = c && (c.isWild || c.wild);
      const r = (c && (c.rank !== undefined ? c.rank : c.r)) || 0;
      if (isW || r === 5) fives++;
      else if (r >= 10) tens++;
      else if (r <= 4) lows++;
    }
    v += fives * 0.85;
    v += Math.min(fives, tens) * 0.25;
    v += Math.min(lows, 3) * 0.12;
    return v;
  }

  /**
   * Finds the optimal partition of 8 cards into two 4-card hands maximizing total points.
   * Both hands are evaluated with the 5-card flush rule (flush only if cut card matches suit).
   * @param {Array} cards8 - Exactly 8 cards combining dealer kept cards and crib cards
   * @param {Object|Array} starter - Starter cut card(s)
   * @param {Object} [options] - Game scoring options
   * @returns {{ hand1: Array, hand2: Array, score1: Object, score2: Object, total: number }}
   */
  static findOptimalTwistedSplit(cards8, starter, options = {}) {
    if (!Array.isArray(cards8) || cards8.length !== 8) {
      const mid = Math.floor((cards8 ? cards8.length : 0) / 2);
      const h1 = cards8 ? cards8.slice(0, mid) : [];
      const h2 = cards8 ? cards8.slice(mid) : [];
      const scoreOpts = { ...options, optFiveCardFlushOnly: true };
      const s1 = CribbageScorer.scoreHand(h1, starter, true, scoreOpts);
      const s2 = CribbageScorer.scoreHand(h2, starter, true, { ...scoreOpts, dealerHand: h1 });
      return { hand1: h1, hand2: h2, score1: s1, score2: s2, total: s1.total + s2.total };
    }

    const scoreOpts = { ...options, optFiveCardFlushOnly: true };
    const lowball = options.goal === 'low';
    let best = null;

    // Fix cards8[0] into hand1 and choose 3 other cards from indices 1..7 (35 partitions)
    // The remaining 4 cards form hand2.
    for (let i = 1; i <= 5; i++) {
      for (let j = i + 1; j <= 6; j++) {
        for (let k = j + 1; k <= 7; k++) {
          const hand1 = [cards8[0], cards8[i], cards8[j], cards8[k]];
          const hand2 = [];
          for (let m = 1; m <= 7; m++) {
            if (m !== i && m !== j && m !== k) {
              hand2.push(cards8[m]);
            }
          }

          const s1 = CribbageScorer.scoreHand(hand1, starter, true, scoreOpts);
          const s2 = CribbageScorer.scoreHand(hand2, starter, true, { ...scoreOpts, dealerHand: hand1 });
          const total = s1.total + s2.total;

          if (!best) {
            best = { hand1, hand2, score1: s1, score2: s2, total };
          } else {
            const isBetter = lowball ? (total < best.total) : (total > best.total);
            if (isBetter) {
              best = { hand1, hand2, score1: s1, score2: s2, total };
            } else if (total === best.total) {
              const maxCurr = Math.max(s1.total, s2.total);
              const maxBest = Math.max(best.score1.total, best.score2.total);
              if (lowball ? (maxCurr < maxBest) : (maxCurr > maxBest)) {
                best = { hand1, hand2, score1: s1, score2: s2, total };
              }
            }
          }
        }
      }
    }

    if (best) {
      const swap = lowball ? (best.score2.total < best.score1.total) : (best.score2.total > best.score1.total);
      if (swap) {
        best = {
          hand1: best.hand2,
          hand2: best.hand1,
          score1: best.score2,
          score2: best.score1,
          total: best.total
        };
      }
      best.hand1 = best.hand1.slice().sort((a, b) => cardRank(a) - cardRank(b));
      best.hand2 = best.hand2.slice().sort((a, b) => cardRank(a) - cardRank(b));
    }

    return best;
  }
}
